import type {IOrderRepository} from '../../domain/repositories/IOrderRepository';
import {Order, OrderCreateParams, OrderState, OrderStatus, PaymentType, type UserOrdersResponse} from '../../domain/entities/Order';
import axios from 'axios';

export class ApiOrderRepository implements IOrderRepository {
    private readonly baseUrl: string;
    private readonly apiClient;

    constructor(baseUrl: string = process.env.NEXT_PUBLIC_API_BASE_URL || 'https://api-staging.motito.co') {
        this.baseUrl = baseUrl;
        this.apiClient = axios.create({
            baseURL: this.baseUrl,
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    findByOrderNo(_orderNo: number): Promise<Order | null> {
        throw new Error("Method not implemented.");
    }

    async create(order: OrderCreateParams): Promise<Order> {
        const response = await this.apiClient.post('/v1/orders', this.mapToApiFormat(order));
        return this.mapFromApiResponse(response.data);
    }

    async findById(id: string): Promise<Order | null> {
        try {
            const response = await this.apiClient.get(`/v1/orders/${id}`);
            return this.mapFromApiResponse(response.data);
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            throw error;
        }
    }

    async update(id: string, updates: Partial<any>): Promise<any> {
        const response = await this.apiClient.patch(`/v1/orders/${id}`, this.mapToApiFormat(updates));
        return this.mapFromApiResponse(response.data);
    }

    async delete(id: string): Promise<void> {
        await this.apiClient.delete(`/v1/orders/${id}`);
    }

    async exists(id: string): Promise<boolean> {
        try {
            await this.apiClient.head(`/v1/orders/${id}`);
            return true;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return false;
            }
            throw error;
        }
    }

    async findAll(filters?: any): Promise<UserOrdersResponse> {
        const params = this.buildQueryParams(filters);
        // Consumer apps should list orders via the user dashboard endpoint.
        // `/v1/orders` is create/retrieve-only for consumers.
        const response = await this.apiClient.get('/v1/users/me/orders', {
            params,
        });
        const payload = response.data as any;
        const results = Array.isArray(payload?.results)
            ? payload.results.map((item: any) => this.mapFromApiResponse(item))
            : [];

        return {
            ...(payload ?? {}),
            results,
        } as UserOrdersResponse;
    }

    // User-specific methods
    async findByUserId(_userId: string, filters?: any): Promise<Order[]> {
        const params = this.buildQueryParams(filters);
        const response = await this.apiClient.get('/v1/users/me/orders', {
            params,
        });
        return response.data.results.map((item: any) => this.mapFromApiResponse(item));
    }

    async getOrderSummary(_userId: string): Promise<any> {
        const response = await this.apiClient.get('/v1/users/me/orders/summary');
        return response.data;
    }

    // Order status management
    async updateStatus(id: string, status: OrderStatus, state?: OrderState): Promise<Order> {
        const response = await this.apiClient.patch(`/v1/orders/${id}`, {
            status: this.mapOrderStatusToApi(status),
            state: state ? this.mapOrderStateToApi(state) : undefined
        });
        return this.mapFromApiResponse(response.data);
    }

    // Delivery tracking
    async getTrackingInfo(orderId: string): Promise<any> {
        const response = await this.apiClient.get(`/v1/orders/${orderId}/tracking`);
        return response.data;
    }

    private buildQueryParams(filters?: any): any {
        if (!filters) return {};

        const params: any = {};

        if (filters.status) {
            params.status = filters.status;
        }
        if (filters.fundingState) {
            params.funding_state = filters.fundingState;
        }
        if (filters.paymentMethod) {
            params.payment_method = filters.paymentMethod;
        }
        if (filters.dateFrom) {
            params.date_from = filters.dateFrom;
        }
        if (filters.dateTo) {
            params.date_to = filters.dateTo;
        }
        if (filters.q) {
            params.q = filters.q;
        }
        if (filters.sort) {
            params.sort = filters.sort;
        }
        if (filters.limit) {
            params.limit = filters.limit;
        }
        if (filters.offset) {
            params.offset = filters.offset;
        }

        return params;
    }

    private mapFromApiResponse(apiData: any): Order {
        return (apiData);
    }

    private mapToApiFormat(orderData: Partial<OrderCreateParams>): any {
        return {
            product_id: orderData.productId,
            product_name: orderData.productName,
            product_price: orderData.productPrice,
            product_adjusted_price: orderData.productAdjustedPrice,
            product_total_price: orderData.productTotalPrice,
            image_url: orderData.imageUrl,
            merchant_name: orderData.merchantName,
            shop_name: orderData.shopName,
            payment_type: orderData.paymentType,
            plan_duration: orderData.planDuration,
            plan_frequency: orderData.planFrequency,
            keep_my_price: orderData.keepMyPrice,
            keep_my_price_fee: orderData.keepMyPriceFee,
            status: orderData.status ? this.mapOrderStatusToApi(orderData.status) : undefined,
            state: orderData.state ? this.mapOrderStateToApi(orderData.state) : undefined,
            phone_number: orderData.phoneNumber,
            platform: orderData.platform,
            user_name: orderData.userName,
            created_by: orderData.createdBy
        };
    }

    private mapApiPaymentTypeToDomain(apiPaymentType: string): PaymentType {
        switch (apiPaymentType) {
            case 'pay_in_full':
                return PaymentType.FULL_PAYMENT;
            case 'pss':
            case 'paysmallsmall':
            case 'installment':
                return PaymentType.INSTALLMENT;
            case 'pay_later':
            case 'pss_credit':
                return PaymentType.PAY_LATER;
            default:
                return PaymentType.FULL_PAYMENT;
        }
    }

    private mapApiStatusToDomain(apiStatus: string): OrderStatus {
        switch (apiStatus) {
            case 'pending':
            case 'pending_payment':
                return OrderStatus.PENDING;
            case 'confirmed':
            case 'payment_confirmed':
                return OrderStatus.CONFIRMED;
            case 'processing':
            case 'preparing':
                return OrderStatus.PROCESSING;
            case 'shipped':
            case 'out_for_delivery':
                return OrderStatus.SHIPPED;
            case 'delivered':
                return OrderStatus.DELIVERED;
            case 'cancelled':
                return OrderStatus.CANCELLED;
            case 'refunded':
                return OrderStatus.REFUNDED;
            default:
                return OrderStatus.PENDING;
        }
    }

    private mapApiStateToDomain(apiState: string): OrderState {
        switch (apiState) {
            case 'initiated':
                return OrderState.INITIATED;
            case 'payment_pending':
                return OrderState.PAYMENT_PENDING;
            case 'payment_confirmed':
                return OrderState.PAYMENT_CONFIRMED;
            case 'fulfillment':
            case 'processing':
                return OrderState.FULFILLMENT;
            case 'completed':
            case 'delivered':
                return OrderState.COMPLETED;
            case 'failed':
            case 'cancelled':
                return OrderState.FAILED;
            default:
                return OrderState.INITIATED;
        }
    }

    private mapOrderStatusToApi(status: OrderStatus): string {
        switch (status) {
            case OrderStatus.PENDING:
                return 'pending';
            case OrderStatus.CONFIRMED:
                return 'confirmed';
            case OrderStatus.PROCESSING:
                return 'processing';
            case OrderStatus.SHIPPED:
                return 'shipped';
            case OrderStatus.DELIVERED:
                return 'delivered';
            case OrderStatus.CANCELLED:
                return 'cancelled';
            case OrderStatus.REFUNDED:
                return 'refunded';
            default:
                return 'pending';
        }
    }

    private mapOrderStateToApi(state: OrderState): string {
        switch (state) {
            case OrderState.INITIATED:
                return 'initiated';
            case OrderState.PAYMENT_PENDING:
                return 'payment_pending';
            case OrderState.PAYMENT_CONFIRMED:
                return 'payment_confirmed';
            case OrderState.FULFILLMENT:
                return 'fulfillment';
            case OrderState.COMPLETED:
                return 'completed';
            case OrderState.FAILED:
                return 'failed';
            default:
                return 'initiated';
        }
    }

    private getAuthToken(): string {
        // In a real implementation, this would get the token from your auth store
        // For now, return empty string for unauthenticated requests
        return '';
    }
}
